import type { FormEvent } from "react";
import { useEffect, useState } from "react";
import { RELATION_TYPES } from "@madamuh/domain";
import type { GuardianLinkRequestDto, RelationType } from "@madamuh/domain";
import { Link, useParams } from "react-router-dom";
import { createGuardianLinkRequest } from "../../shared/api/guardian";
import { useSessionBootstrap } from "../../shared/session/SessionBootstrapProvider";
import { PageScaffold } from "../../shared/ui/PageScaffold";

export function LinkStudentRequestPage() {
  const { tenantSlug = "unknown-tenant" } = useParams();
  const { status, errorMessage, bootstrapSession } = useSessionBootstrap();
  const [requestedRelationType, setRequestedRelationType] = useState<RelationType>("parent");
  const [studentName, setStudentName] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [supportingInfo, setSupportingInfo] = useState("");
  const [requestRecord, setRequestRecord] = useState<GuardianLinkRequestDto | null>(null);
  const [submitState, setSubmitState] = useState<"idle" | "submitting" | "error">("idle");
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (status === "idle") {
      void bootstrapSession(tenantSlug);
    }
  }, [bootstrapSession, status, tenantSlug]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitState("submitting");
    setSubmitError(null);

    try {
      const createdRequest = await createGuardianLinkRequest(tenantSlug, {
        requestedRelationType,
        studentName,
        birthDate: birthDate || null,
        supportingInfo: supportingInfo || null
      });

      setRequestRecord(createdRequest);
      setSubmitState("idle");
    } catch (error) {
      setSubmitState("error");
      setSubmitError(error instanceof Error ? error.message : "申請の送信に失敗しました");
    }
  }

  return (
    <PageScaffold
      title="Link Student Request"
      description="招待を持っていない場合の保護者紐付け申請です。現在は in-memory request list に登録され、admin review ページから approve / reject できます。"
      links={[
        { to: "/t/:tenantSlug/link-student", label: "招待入力へ戻る" },
        { to: "/t/:tenantSlug/entry", label: "Entryへ戻る" }
      ]}
    >
      <div style={{ display: "grid", gap: 16 }}>
        {status === "loading" ? <p>セッションを確認しています。</p> : null}
        {status === "unauthenticated" ? (
          <p>
            申請には session が必要です。<Link to={`/t/${tenantSlug}/entry`}>/entry</Link> で mock login を実行してください。
          </p>
        ) : null}
        {status === "error" ? <p>セッション取得に失敗しました: {errorMessage}</p> : null}

        {status === "authenticated" ? (
          <form onSubmit={handleSubmit} style={{ display: "grid", gap: 12 }}>
            <label style={{ display: "grid", gap: 4 }}>
              <span>relation type</span>
              <select
                value={requestedRelationType}
                onChange={(event) => setRequestedRelationType(event.target.value as RelationType)}
              >
                {RELATION_TYPES.map((relationType) => (
                  <option key={relationType} value={relationType}>
                    {relationType}
                  </option>
                ))}
              </select>
            </label>

            <label style={{ display: "grid", gap: 4 }}>
              <span>student name</span>
              <input
                value={studentName}
                onChange={(event) => setStudentName(event.target.value)}
                placeholder="Anna Demo"
                required
              />
            </label>

            <label style={{ display: "grid", gap: 4 }}>
              <span>birth date</span>
              <input value={birthDate} onChange={(event) => setBirthDate(event.target.value)} type="date" />
            </label>

            <label style={{ display: "grid", gap: 4 }}>
              <span>supporting info</span>
              <textarea
                value={supportingInfo}
                onChange={(event) => setSupportingInfo(event.target.value)}
                rows={4}
                placeholder="兄弟姉妹名や所属クラスなど"
              />
            </label>

            <button type="submit" disabled={submitState === "submitting"}>
              {submitState === "submitting" ? "送信中..." : "申請を送信する"}
            </button>
          </form>
        ) : null}

        {submitError ? <p>{submitError}</p> : null}

        {requestRecord ? (
          <div style={{ display: "grid", gap: 8 }}>
            <p style={{ margin: 0 }}>申請を登録しました。</p>
            <p style={{ margin: 0 }}>requestId: {requestRecord.requestId}</p>
            <p style={{ margin: 0 }}>status: {requestRecord.status}</p>
            <p style={{ margin: 0 }}>
              demo admin review: <Link to={`/t/${tenantSlug}/teacher/admin/guardian-link-requests`}>一覧を見る</Link>
            </p>
          </div>
        ) : null}
      </div>
    </PageScaffold>
  );
}
